import { useState, useEffect, useRef } from 'react';

interface UseHideOnScrollOptions {
  disabled?: boolean;
  headerRef?: React.RefObject<HTMLElement>;
  scrollBuffer?: number;
  topThreshold?: number;
}

/**
 * Returns `true` when the nav should be hidden (user scrolling down).
 * Uses passive scroll listeners + rAF throttling for zero INP impact.
 */
export function useHideOnScroll({
  disabled = false,
  headerRef,
  scrollBuffer = 80,
  topThreshold = 100,
}: UseHideOnScrollOptions = {}): boolean {
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const accumulatedDelta = useRef(0);
  const rafId = useRef<number | null>(null);

  // Sync ref updated during render (not in an effect) so the scroll
  // handler always reads the *current* disabled value, even if the
  // closure captured a stale one before effects had a chance to clean up.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Reset state when disabled changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    lastScrollY.current = window.scrollY;
    accumulatedDelta.current = 0;
    if (disabled) {
      setHidden(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (typeof window === 'undefined' || disabled) return;

    lastScrollY.current = window.scrollY;
    accumulatedDelta.current = 0;

    const onScroll = () => {
      // Early bail-out using the live ref — prevents even queuing a rAF
      // when disabled changed mid-frame (e.g. flushSync + focus scroll).
      if (disabledRef.current) return;
      if (rafId.current !== null) return; // one pending rAF at a time

      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;

        // Double-check the live ref inside the rAF callback too,
        // in case disabled changed between queuing and executing.
        if (disabledRef.current) return;

        const currentY = window.scrollY;

        // Always show near page top
        if (currentY < topThreshold) {
          setHidden(false);
          lastScrollY.current = currentY;
          accumulatedDelta.current = 0;
          return;
        }

        // Don't hide while the user is actively typing in a non-empty
        // search input inside the header. Buttons, links, and empty
        // inputs (e.g. after clearing with ×) don't block hiding.
        if (
          headerRef?.current &&
          document.activeElement instanceof HTMLElement &&
          headerRef.current.contains(document.activeElement)
        ) {
          const ae = document.activeElement;
          const isActiveInput =
            (ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement) &&
            ae.value.trim().length > 0;
          if (isActiveInput) {
            lastScrollY.current = currentY;
            accumulatedDelta.current = 0;
            return;
          }
        }

        const delta = currentY - lastScrollY.current;
        lastScrollY.current = currentY;

        // Accumulate scroll in one direction, reset on direction change
        if ((accumulatedDelta.current > 0 && delta < 0) || (accumulatedDelta.current < 0 && delta > 0)) {
          accumulatedDelta.current = 0;
        }
        accumulatedDelta.current += delta;

        if (accumulatedDelta.current > scrollBuffer) {
          setHidden(true);
          accumulatedDelta.current = 0;
        } else if (accumulatedDelta.current < -scrollBuffer) {
          setHidden(false);
          accumulatedDelta.current = 0;
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [disabled, headerRef, scrollBuffer, topThreshold]);

  return hidden;
}
